import { Box, Typography, Button } from "@mui/material";
import DownArrowsIcon from "assets/icons/dashboard/DownArrowsIcon";
import UpArrowsIcon from "assets/icons/dashboard/UpArrowsIcon";
import PropTypes from "prop-types";

interface StatCardProps {
  title: string;
  value: number | string;
  change: number;
  changeText: string;
  actionText: string;
  onActionClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  changeText,
  actionText,
  onActionClick,
}) => {
  const isPositive = change >= 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "16px",
        gap: "10px",
        width: "100%",
        height: "197px",
        backgroundColor: "#FFFFFF",
        border: "1px solid #E8E8E9",
        borderRadius: "12px",
        flex: "1 1 0",
      }}
    >
      {/* Title */}
      <Typography
        variant="h6"
        sx={{
          width: "100%",
          height: "24px",
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: "24px",
          letterSpacing: "0.15px",
          color: "#757575",
          flex: "none",
          order: 0,
          alignSelf: "stretch",
          flexGrow: 0,
        }}
      >
        {title}
      </Typography>

      {/* Value */}
      <Typography
        variant="h4"
        sx={{
          width: "100%",
          fontWeight: 400,
          height: "36px",
          fontSize: "22px",
          lineHeight: "28px",
          color: "#1B1B22",
          flex: "none",
          order: 1,
          alignSelf: "stretch",
          flexGrow: 0,
          marginTop: 0,
          paddingTop: 0,
        }}
      >
        {value}
      </Typography>

      {/* Change indicator container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: "0px",
          gap: "10px",
          width: "100%",
          height: "16px",
          flex: "none",
          order: 2,
          alignSelf: "stretch",
          flexGrow: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: "0px",
            gap: "10px",
            width: "100%",
            height: "16px",
            flex: "none",
            order: 0,
            alignSelf: "stretch",
            flexGrow: 0,
          }}
        >
          {/* Change percentage with arrow */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "0px",
              gap: "4px",
              height: "16px",
              flex: "none",
              order: 0,
              flexGrow: 1,
            }}
          >
            {/* Arrow icon */}
            <Box
              sx={{
                width: "16px",
                height: "16px",
                flex: "none",
                order: 0,
                flexGrow: 0,
              }}
            >
              {isPositive ? <UpArrowsIcon /> : <DownArrowsIcon />}
            </Box>

            {/* Percentage text */}
            <Typography
              sx={{
                width: "40px",
                height: "16px",
                fontWeight: 500,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.4px",
                color: isPositive ? "#02542D" : "#B21212",
                flex: "none",
                order: 1,
                flexGrow: 0,
              }}
            >
              {Math.abs(change)}%
            </Typography>
          </Box>

          {/* Since last week text */}
          <Typography
            sx={{
              height: "16px",
              fontWeight: 400,
              fontSize: "12px",
              lineHeight: "16px",
              color: "#757575",
              letterSpacing: "0.4px",
              alignItems: "flex-end",
              order: 1,
              flexGrow: 0,
            }}
          >
            {changeText}
          </Typography>
        </Box>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "100%",
          height: "1px",
          backgroundColor: "#D9D9D9",
          flex: "none",
          order: 3,
          alignSelf: "stretch",
          flexGrow: 0,
        }}
      />

      {/* Action Button */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          padding: "0px",
          gap: "10px",
          width: "100%",
          height: "48px",
          flex: "none",
          order: 4,
          alignSelf: "stretch",
          flexGrow: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
            height: "48px",
            flex: "none",
            order: 0,
            flexGrow: 0,
          }}
        >
          <Button
            onClick={onActionClick}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "center",
              padding: "0px",
              backgroundColor: "transparent",
              border: "none",
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
              textTransform: "none",
              minWidth: "auto",
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.1px",
                color: "#000080",
                textAlign: "right",
                paddingRight: "8px",
              }}
            >
              {actionText}
            </Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  change: PropTypes.number.isRequired,
  changeText: PropTypes.string.isRequired,
  actionText: PropTypes.string.isRequired,
  onActionClick: PropTypes.func,
};

export default StatCard;
